import type { ReactNode } from "react";

import classNames from "classnames";
import { useRef } from "react";
import { Offcanvas } from "react-bootstrap";

import styles from "./BottomSheet.module.scss";

interface IBottomSheetProps {
  show: boolean;
  onHide: () => void;
  title?: ReactNode;
  swipeThreshold?: number;
  maxHeight?: string;
  content?: ReactNode;
}

function BottomSheet({
  show,
  onHide,
  title,
  swipeThreshold = 80,
  maxHeight,
  content,
}: IBottomSheetProps) {
  const touchStartY = useRef(0);
  const touchStartTime = useRef(0);
  const bodyRef = useRef<HTMLDivElement>(null);

  const handleTouchStart = (e: React.TouchEvent) => {
    touchStartY.current = e.touches[0].clientY;
    touchStartTime.current = Date.now();
  };

  const handleTouchEnd = (e: React.TouchEvent) => {
    const deltaY = e.changedTouches[0].clientY - touchStartY.current;
    const velocity = deltaY / (Date.now() - touchStartTime.current); // px/ms
    const isScrolledToTop = !bodyRef.current || bodyRef.current.scrollTop === 0;

    if (isScrolledToTop && (velocity > 0.5 || deltaY > swipeThreshold)) {
      onHide();
    }
  };

  return (
    <Offcanvas
      show={show}
      onHide={onHide}
      placement="bottom"
      className={classNames("rounded-top-4", styles.bottomSheet)}
      style={
        maxHeight
          ? ({ "--bottom-sheet-max-height": maxHeight } as React.CSSProperties)
          : undefined
      }
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
    >
      <div className="d-flex justify-content-center pt-2">
        <div
          className={classNames(
            "bg-secondary-subtle rounded",
            styles.dragHandle
          )}
        />
      </div>
      {title && (
        <Offcanvas.Header className="d-flex justify-content-center pb-3 px-4">
          <Offcanvas.Title>{title}</Offcanvas.Title>
        </Offcanvas.Header>
      )}
      {content && (
        <Offcanvas.Body className="pt-0 px-4 overflow-y-auto" ref={bodyRef}>
          {content}
        </Offcanvas.Body>
      )}
    </Offcanvas>
  );
}

export default BottomSheet;
